/**
 * Custom React Query hooks for blocking/unblocking users
 *
 * These hooks call the workflow endpoints:
 * - POST /api/workflows/connections/block
 * - POST /api/workflows/connections/unblock
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000';

interface BlockUserRequest {
  user_id: string;
}

interface BlockUserResponse {
  success: boolean;
  message: string;
  data: {
    blockedUserId: string;
    blockedAt: string;
  };
}

interface UnblockUserResponse {
  success: boolean;
  message: string;
  data: {
    unblockedUserId: string;
    unblockedAt: string;
  };
}

/**
 * Hook to block a user
 *
 * @example
 * const blockUser = useBlockUser();
 *
 * const handleBlock = async (userId: string) => {
 *   await blockUser.mutateAsync({ user_id: userId });
 * };
 */
export const useBlockUser = () => {
  const queryClient = useQueryClient();

  return useMutation<BlockUserResponse, Error, BlockUserRequest>({
    mutationFn: async (data: BlockUserRequest) => {
      console.log('[useBlockUser] mutationFn called with data:', data);
      console.log('[useBlockUser] API_URL:', API_URL);
      console.log('[useBlockUser] Full URL:', `${API_URL}/api/workflows/connections/block`);

      const response = await axios.post(
        `${API_URL}/api/workflows/connections/block`,
        data,
        {
          withCredentials: true,
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );

      console.log('[useBlockUser] Response received:', response.data);
      return response.data;
    },
    onSuccess: async () => {
      // Refetch connections data immediately to ensure blocked users are updated
      await queryClient.refetchQueries({ queryKey: ['/api/workflows/connections'] });
      // Also invalidate other related queries
      queryClient.invalidateQueries({ queryKey: ['/api/workflows/connections/pending'] });
      queryClient.invalidateQueries({ queryKey: ['/api/partners/suggested'] });
    },
  });
};

/**
 * Hook to unblock a user
 *
 * @example
 * const unblockUser = useUnblockUser();
 *
 * const handleUnblock = async (userId: string) => {
 *   await unblockUser.mutateAsync({ user_id: userId });
 * };
 */
export const useUnblockUser = () => {
  const queryClient = useQueryClient();

  return useMutation<UnblockUserResponse, Error, BlockUserRequest>({
    mutationFn: async (data: BlockUserRequest) => {
      console.log('[useUnblockUser] Data received:', data);
      console.log('[useUnblockUser] Data type:', typeof data);
      console.log('[useUnblockUser] Data JSON:', JSON.stringify(data));

      const response = await axios.post(
        `${API_URL}/api/workflows/connections/unblock`,
        data,
        {
          withCredentials: true,
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );
      return response.data;
    },
    onSuccess: async () => {
      // Refetch connections data immediately to ensure unblocked users are updated
      await queryClient.refetchQueries({ queryKey: ['/api/workflows/connections'] });
      // Also invalidate other related queries
      queryClient.invalidateQueries({ queryKey: ['/api/workflows/connections/pending'] });
      queryClient.invalidateQueries({ queryKey: ['/api/partners/suggested'] });
    },
  });
};

/**
 * Hook to fetch blocked users list
 *
 * @example
 * const { data: blockedUsers, isLoading } = useBlockedUsers();
 */
export const useBlockedUsers = () => {
  const queryClient = useQueryClient();

  // We can derive this from the connections query by filtering for blocked status
  // Or we could create a dedicated endpoint if needed
  return {
    queryKey: ['blockedUsers'],
    queryFn: async () => {
      const response = await axios.get(
        `${API_URL}/api/workflows/connections`,
        {
          withCredentials: true,
        }
      );

      // Filter for blocked connections where current user is the blocker
      // (blocked users will have user_id = blocked user, connected_user_id = current user)
      return response.data.filter((conn: any) => conn.status === 'blocked');
    },
  };
};
